// Vollea — Organiser Tools modal. One-stop shop for manual overrides an
// organiser or club-admin might need mid-season:
//   • Members tab   → add existing user / add guest player / remove member.
//   • Teams tab     → create team, rename, swap players, change division, delete.
//   • Match tab     → create a match from scratch (with optional final score).
//
// Score override (per-match) is a separate small modal (OrganiserScoreModal)
// mounted directly on each fixture card so it feels contextual.
import React, { useEffect, useState, useCallback } from "react";
import { IoClose, IoPersonAddOutline, IoPeopleOutline, IoTrophyOutline, IoSearch, IoTrash, IoAdd, IoCreateOutline, IoCheckmark } from "react-icons/io5";
import { Avatar } from "./ui-vollea";
import { api } from "../lib/api";
import { C, SP, R } from "../lib/theme";

const TABS = [
  { key: "members", label: "Members", icon: IoPersonAddOutline },
  { key: "teams", label: "Teams", icon: IoPeopleOutline },
  { key: "match", label: "New Match", icon: IoTrophyOutline },
];

export default function OrganiserToolsModal({ league, onClose, onChanged, initialTab = "members" }) {
  const [tab, setTab] = useState(initialTab);
  const [msg, setMsg] = useState(null);
  const flashOk = (t) => { setMsg({ ok: true, text: t }); setTimeout(() => setMsg(null), 2200); };
  const flashErr = (e) => { setMsg({ ok: false, text: e.message || String(e) }); setTimeout(() => setMsg(null), 3600); };

  return (
    <div data-testid="organiser-tools-modal" style={overlay} onClick={onClose}>
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: `${SP.md}px ${SP.lg}px`, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>Organiser Tools</div>
          <button type="button" data-testid="organiser-tools-close" onClick={onClose} style={iconBtn}><IoClose size={22} /></button>
        </div>

        <div style={{ display: "flex", gap: SP.sm, padding: `${SP.md}px ${SP.lg}px 0` }}>
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                data-testid={`organiser-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                style={{
                  flex: 1, height: 40, borderRadius: R.md,
                  background: active ? C.lime : C.surface2,
                  color: active ? C.onLime : C.text,
                  border: `1px solid ${active ? C.lime : C.borderStrong}`,
                  fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: "uppercase",
                  display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, cursor: "pointer",
                }}
              >
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>

        {msg ? (
          <div data-testid="organiser-tools-msg" style={{ margin: `${SP.sm}px ${SP.lg}px 0`, padding: `${SP.sm}px ${SP.md}px`, borderRadius: R.sm, background: msg.ok ? C.limeTint : "rgba(239,68,68,0.1)", color: msg.ok ? C.lime : C.error, border: `1px solid ${msg.ok ? C.lime : C.error}`, fontSize: 12, fontWeight: 600 }}>{msg.text}</div>
        ) : null}

        <div style={{ padding: SP.lg, overflowY: "auto", flex: 1 }}>
          {tab === "members" && <MembersPanel league={league} onOk={flashOk} onErr={flashErr} onChanged={onChanged} />}
          {tab === "teams" && <TeamsPanel league={league} onOk={flashOk} onErr={flashErr} onChanged={onChanged} />}
          {tab === "match" && <NewMatchPanel league={league} onOk={flashOk} onErr={flashErr} onChanged={onChanged} onDone={onClose} />}
        </div>
      </div>
    </div>
  );
}

// ------------------------- MEMBERS PANEL -------------------------
function MembersPanel({ league, onOk, onErr, onChanged }) {
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [division, setDivision] = useState(league.divisions?.[0] || "");
  const [guestName, setGuestName] = useState("");
  const [busy, setBusy] = useState(false);

  const loadMembers = useCallback(async () => {
    try { setMembers(await api.get(`/leagues/${league.league_id}/members`) || []); }
    catch { setMembers([]); }
  }, [league.league_id]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  // Debounced search for existing platform users.
  useEffect(() => {
    const t = setTimeout(() => {
      if (!search.trim()) { setResults([]); return; }
      api.get(`/leagues/${league.league_id}/players/search?q=${encodeURIComponent(search)}`)
        .then((d) => setResults(d || []))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [search, league.league_id]);

  const addExisting = async (userId) => {
    setBusy(true);
    try {
      await api.post(`/leagues/${league.league_id}/players/add`, { user_id: userId, division });
      onOk("Player added");
      setSearch(""); setResults([]);
      await loadMembers();
      onChanged?.();
    } catch (e) { onErr(e); }
    finally { setBusy(false); }
  };

  const addGuest = async () => {
    const name = guestName.trim();
    if (!name) return;
    setBusy(true);
    try {
      await api.post(`/leagues/${league.league_id}/players/add`, { name, division });
      onOk("Guest added");
      setGuestName("");
      await loadMembers();
      onChanged?.();
    } catch (e) { onErr(e); }
    finally { setBusy(false); }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.name || m.email} from this league? Any team they're in will also be removed.`)) return;
    setBusy(true);
    try {
      await api.del(`/leagues/${league.league_id}/players/${m.user_id}`);
      onOk("Removed");
      await loadMembers();
      onChanged?.();
    } catch (e) { onErr(e); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SP.lg }}>
      <DivisionPicker league={league} value={division} onChange={setDivision} testid="members-division-picker" />

      <section>
        <SectionLbl>Add existing player</SectionLbl>
        <div style={{ position: "relative", marginTop: SP.sm }}>
          <IoSearch size={16} color={C.textMuted} style={{ position: "absolute", left: 14, top: 18 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            data-testid="member-search-input"
            style={{ ...inputStyle, paddingLeft: 40 }}
          />
        </div>
        {results.length > 0 && (
          <div style={{ marginTop: SP.sm, display: "flex", flexDirection: "column", gap: 4, maxHeight: 240, overflowY: "auto" }}>
            {results.map((r) => (
              <div key={r.user_id} style={rowStyle}>
                <Avatar uri={r.photo} name={r.name} size={32} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>
                    {r.name} {r.is_guest ? <span style={guestPill}>guest</span> : null}
                  </div>
                  <div style={{ color: C.textMuted, fontSize: 11 }}>{r.email || "—"}</div>
                </div>
                <button
                  type="button"
                  data-testid={`member-add-${r.user_id}`}
                  disabled={busy || r.is_member}
                  onClick={() => addExisting(r.user_id)}
                  style={r.is_member ? btnGhost : btnLimeSm}
                >
                  {r.is_member ? "Member" : "Add"}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <SectionLbl>Add guest player <span style={{ color: C.textDim, fontSize: 10, marginLeft: 6 }}>(no login)</span></SectionLbl>
        <div style={{ display: "flex", gap: SP.sm, marginTop: SP.sm }}>
          <input
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            placeholder="Guest name (e.g. James Smith)"
            data-testid="guest-name-input"
            style={inputStyle}
          />
          <button
            type="button"
            data-testid="guest-add-btn"
            disabled={busy || !guestName.trim()}
            onClick={addGuest}
            style={{ ...btnLime, minWidth: 80, opacity: guestName.trim() ? 1 : 0.4 }}
          >
            <IoAdd size={16} /> Add
          </button>
        </div>
      </section>

      <section>
        <SectionLbl>Current members <span style={{ color: C.textDim, fontSize: 10, marginLeft: 6 }}>({members.length})</span></SectionLbl>
        <div style={{ marginTop: SP.sm, display: "flex", flexDirection: "column", gap: 4, maxHeight: 260, overflowY: "auto" }} data-testid="members-list">
          {members.length === 0 ? (
            <div style={{ color: C.textMuted, fontSize: 12, padding: SP.md, textAlign: "center" }}>No members yet.</div>
          ) : members.map((m) => (
            <div key={m.user_id} style={rowStyle} data-testid={`member-row-${m.user_id}`}>
              <Avatar uri={m.photo} name={m.name} size={32} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: C.text, fontSize: 13, fontWeight: 600 }}>
                  {m.name} {m.is_guest ? <span style={guestPill}>guest</span> : null}
                </div>
                <div style={{ color: C.textMuted, fontSize: 11 }}>Div {m.division}</div>
              </div>
              <button type="button" data-testid={`member-remove-${m.user_id}`} onClick={() => removeMember(m)} disabled={busy} style={btnDangerSm}>
                <IoTrash size={14} />
              </button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

// ------------------------- TEAMS PANEL -------------------------
function TeamsPanel({ league, onOk, onErr, onChanged }) {
  const [division, setDivision] = useState(league.divisions?.[0] || "");
  const [members, setMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [aId, setAId] = useState("");
  const [bId, setBId] = useState("");
  const [teamName, setTeamName] = useState("");
  const [editing, setEditing] = useState(null); // team_id
  const [editState, setEditState] = useState({ name: "", a: "", b: "", division: "" });
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const [mems, tms] = await Promise.all([
      api.get(`/leagues/${league.league_id}/members`).catch(() => []),
      api.get(`/leagues/${league.league_id}/teams`).catch(() => []),
    ]);
    setMembers(mems || []);
    setTeams(tms || []);
  }, [league.league_id]);
  useEffect(() => { reload(); }, [reload]);

  const divisionMembers = members.filter((m) => m.division === division);
  const divisionTeams = teams.filter((t) => t.division === division);

  const createTeam = async () => {
    if (!aId || !bId || aId === bId) { onErr(new Error("Pick two different players")); return; }
    setBusy(true);
    try {
      await api.post(`/leagues/${league.league_id}/teams`, {
        player_ids: [aId, bId], division, name: teamName.trim() || undefined,
      });
      onOk("Team created");
      setAId(""); setBId(""); setTeamName("");
      await reload();
      onChanged?.();
    } catch (e) { onErr(e); }
    finally { setBusy(false); }
  };

  const beginEdit = (t) => {
    setEditing(t.team_id);
    setEditState({ name: t.name || "", a: t.player_ids[0], b: t.player_ids[1], division: t.division });
  };
  const saveEdit = async () => {
    setBusy(true);
    try {
      const patch = {
        name: editState.name.trim() || undefined,
        player_ids: [editState.a, editState.b],
        division: editState.division,
      };
      await api.put(`/leagues/${league.league_id}/teams/${editing}`, patch);
      onOk("Team updated");
      setEditing(null);
      await reload();
      onChanged?.();
    } catch (e) { onErr(e); }
    finally { setBusy(false); }
  };
  const removeTeam = async (t) => {
    if (!window.confirm(`Delete team "${t.name}"?`)) return;
    setBusy(true);
    try {
      await api.del(`/leagues/${league.league_id}/teams/${t.team_id}`);
      onOk("Team deleted");
      await reload();
      onChanged?.();
    } catch (e) { onErr(e); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SP.lg }}>
      <DivisionPicker league={league} value={division} onChange={setDivision} testid="teams-division-picker" />

      <section>
        <SectionLbl>Create team</SectionLbl>
        <input value={teamName} onChange={(e) => setTeamName(e.target.value)} placeholder="Team name (optional)" data-testid="team-name-input" style={{ ...inputStyle, marginTop: SP.sm }} />
        <div style={{ display: "flex", gap: SP.sm, marginTop: SP.sm }}>
          <PlayerSelect value={aId} onChange={setAId} members={divisionMembers} exclude={bId} placeholder="Player 1" testid="team-p1-select" />
          <PlayerSelect value={bId} onChange={setBId} members={divisionMembers} exclude={aId} placeholder="Player 2" testid="team-p2-select" />
        </div>
        <button type="button" data-testid="team-create-btn" disabled={busy || !aId || !bId} onClick={createTeam} style={{ ...btnLime, width: "100%", marginTop: SP.sm, opacity: aId && bId ? 1 : 0.4 }}>
          <IoAdd size={16} /> Create team
        </button>
      </section>

      <section>
        <SectionLbl>Teams in Div {division} <span style={{ color: C.textDim, fontSize: 10, marginLeft: 6 }}>({divisionTeams.length})</span></SectionLbl>
        <div style={{ marginTop: SP.sm, display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto" }} data-testid="teams-list">
          {divisionTeams.length === 0 ? (
            <div style={{ color: C.textMuted, fontSize: 12, padding: SP.md, textAlign: "center" }}>No teams in this division yet.</div>
          ) : divisionTeams.map((t) => (
            editing === t.team_id ? (
              <div key={t.team_id} style={{ ...rowStyle, flexDirection: "column", alignItems: "stretch", gap: SP.sm }} data-testid={`team-edit-${t.team_id}`}>
                <input value={editState.name} onChange={(e) => setEditState({ ...editState, name: e.target.value })} placeholder="Team name" style={inputStyle} data-testid={`team-edit-name-${t.team_id}`} />
                <div style={{ display: "flex", gap: SP.sm }}>
                  <PlayerSelect value={editState.a} onChange={(v) => setEditState({ ...editState, a: v })} members={members} exclude={editState.b} placeholder="Player 1" testid={`team-edit-p1-${t.team_id}`} />
                  <PlayerSelect value={editState.b} onChange={(v) => setEditState({ ...editState, b: v })} members={members} exclude={editState.a} placeholder="Player 2" testid={`team-edit-p2-${t.team_id}`} />
                </div>
                <select value={editState.division} onChange={(e) => setEditState({ ...editState, division: e.target.value })} style={selectStyle} data-testid={`team-edit-div-${t.team_id}`}>
                  {(league.divisions || []).map((d) => <option key={d} value={d} style={{ background: C.surface }}>Div {d}</option>)}
                </select>
                <div style={{ display: "flex", gap: SP.sm }}>
                  <button type="button" data-testid={`team-edit-save-${t.team_id}`} onClick={saveEdit} disabled={busy} style={{ ...btnLime, flex: 1 }}><IoCheckmark size={16} /> Save</button>
                  <button type="button" data-testid={`team-edit-cancel-${t.team_id}`} onClick={() => setEditing(null)} style={{ ...btnGhost, flex: 1 }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div key={t.team_id} style={rowStyle} data-testid={`team-row-${t.team_id}`}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ color: C.text, fontSize: 13, fontWeight: 700 }}>{t.name}</div>
                  <div style={{ color: C.textMuted, fontSize: 11 }}>
                    {(t.players || []).map((p) => p.name).join(" & ") || t.player_ids.join(" & ")} · {t.played ?? 0} P · {t.points ?? 0} pts
                  </div>
                </div>
                <button type="button" data-testid={`team-edit-btn-${t.team_id}`} onClick={() => beginEdit(t)} style={btnIconSm}><IoCreateOutline size={14} /></button>
                <button type="button" data-testid={`team-delete-btn-${t.team_id}`} onClick={() => removeTeam(t)} disabled={busy} style={btnDangerSm}><IoTrash size={14} /></button>
              </div>
            )
          ))}
        </div>
      </section>
    </div>
  );
}

// ------------------------- NEW MATCH PANEL -------------------------
function NewMatchPanel({ league, onOk, onErr, onChanged, onDone }) {
  const [division, setDivision] = useState(league.divisions?.[0] || "");
  const [teams, setTeams] = useState([]);
  const [teamAId, setTeamAId] = useState("");
  const [teamBId, setTeamBId] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [enterScore, setEnterScore] = useState(false);
  const [sets, setSets] = useState([{ a: "", b: "" }, { a: "", b: "" }, { a: "", b: "" }]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/leagues/${league.league_id}/teams`).then((d) => setTeams(d || [])).catch(() => setTeams([]));
  }, [league.league_id]);

  const divisionTeams = teams.filter((t) => t.division === division);
  const teamA = divisionTeams.find((t) => t.team_id === teamAId);
  const teamB = divisionTeams.find((t) => t.team_id === teamBId);

  const submit = async () => {
    if (!teamA || !teamB || teamA.team_id === teamB.team_id) { onErr(new Error("Pick two different teams")); return; }
    const validSets = sets
      .map((s) => ({ team_a: parseInt(s.a, 10), team_b: parseInt(s.b, 10) }))
      .filter((s) => Number.isFinite(s.team_a) && Number.isFinite(s.team_b));
    setBusy(true);
    try {
      const created = await api.post("/matches", {
        league_id: league.league_id,
        team_a: teamA.player_ids, team_b: teamB.player_ids,
        team_a_id: teamA.team_id, team_b_id: teamB.team_id,
        division, scheduled_at: scheduledAt || undefined,
      });
      if (enterScore && validSets.length > 0) {
        await api.post(`/matches/${created.match_id}/organiser-set-score`, { sets: validSets });
      }
      onOk(enterScore && validSets.length > 0 ? "Match added with result" : "Match created");
      onChanged?.();
      onDone?.();
    } catch (e) { onErr(e); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: SP.md }}>
      <DivisionPicker league={league} value={division} onChange={(d) => { setDivision(d); setTeamAId(""); setTeamBId(""); }} testid="match-division-picker" />
      <div style={{ display: "flex", gap: SP.sm }}>
        <TeamSelect value={teamAId} onChange={setTeamAId} teams={divisionTeams} exclude={teamBId} placeholder="Team A" testid="match-team-a-select" />
        <TeamSelect value={teamBId} onChange={setTeamBId} teams={divisionTeams} exclude={teamAId} placeholder="Team B" testid="match-team-b-select" />
      </div>
      <div>
        <SectionLbl>Scheduled (optional)</SectionLbl>
        <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} data-testid="match-scheduled-input" style={{ ...inputStyle, marginTop: 6 }} />
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: SP.sm, color: C.text, fontSize: 13, cursor: "pointer" }}>
        <input type="checkbox" checked={enterScore} onChange={(e) => setEnterScore(e.target.checked)} data-testid="match-enter-score-toggle" />
        <span>Record final score too <span style={{ color: C.textDim }}>(walkover / offline match)</span></span>
      </label>

      {enterScore && (
        <div>
          <SectionLbl>Set scores</SectionLbl>
          {sets.map((s, i) => (
            <div key={i} style={{ display: "flex", gap: SP.sm, marginTop: SP.sm, alignItems: "center" }}>
              <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, width: 40 }}>SET {i + 1}</div>
              <input inputMode="numeric" value={s.a} onChange={(e) => setSets(sets.map((x, j) => j === i ? { ...x, a: e.target.value.replace(/[^0-9]/g, "") } : x))} placeholder="A" data-testid={`match-set-a-${i}`} style={{ ...inputStyle, textAlign: "center" }} />
              <div style={{ color: C.textDim, fontWeight: 700 }}>–</div>
              <input inputMode="numeric" value={s.b} onChange={(e) => setSets(sets.map((x, j) => j === i ? { ...x, b: e.target.value.replace(/[^0-9]/g, "") } : x))} placeholder="B" data-testid={`match-set-b-${i}`} style={{ ...inputStyle, textAlign: "center" }} />
            </div>
          ))}
        </div>
      )}

      <button type="button" data-testid="match-submit-btn" onClick={submit} disabled={busy || !teamA || !teamB} style={{ ...btnLime, width: "100%", marginTop: SP.sm, opacity: (teamA && teamB) ? 1 : 0.4 }}>
        <IoAdd size={16} /> Add match
      </button>
    </div>
  );
}

// ------------------------- SHARED SUB-COMPONENTS -------------------------
function DivisionPicker({ league, value, onChange, testid }) {
  return (
    <div>
      <SectionLbl>Division</SectionLbl>
      <div style={{ display: "flex", gap: SP.sm, flexWrap: "wrap", marginTop: SP.sm }} data-testid={testid}>
        {(league.divisions || []).map((d) => {
          const active = d === value;
          return (
            <button key={d} type="button" onClick={() => onChange(d)} data-testid={`${testid}-${d}`} style={{
              height: 32, padding: `0 ${SP.md}px`, borderRadius: R.pill,
              background: active ? C.lime : "transparent",
              color: active ? C.onLime : C.text,
              border: `1px solid ${active ? C.lime : C.borderStrong}`,
              fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: "uppercase", cursor: "pointer",
            }}>Div {d}</button>
          );
        })}
      </div>
    </div>
  );
}

function PlayerSelect({ value, onChange, members, exclude, placeholder, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} style={selectStyle}>
      <option value="" style={{ background: C.surface }}>{placeholder}</option>
      {members.filter((m) => m.user_id !== exclude).map((m) => (
        <option key={m.user_id} value={m.user_id} style={{ background: C.surface }}>{m.name}{m.is_guest ? " (guest)" : ""}</option>
      ))}
    </select>
  );
}
function TeamSelect({ value, onChange, teams, exclude, placeholder, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} style={selectStyle}>
      <option value="" style={{ background: C.surface }}>{placeholder}</option>
      {teams.filter((t) => t.team_id !== exclude).map((t) => (
        <option key={t.team_id} value={t.team_id} style={{ background: C.surface }}>{t.name}</option>
      ))}
    </select>
  );
}
function SectionLbl({ children }) {
  return <div style={{ color: C.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" }}>{children}</div>;
}

// ------------------------- STYLES -------------------------
const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 90, display: "flex", alignItems: "flex-end", justifyContent: "center" };
const sheet = { width: "100%", maxWidth: 640, background: C.bg, borderRadius: `${R.lg}px ${R.lg}px 0 0`, borderTop: `1px solid ${C.border}`, maxHeight: "92vh", display: "flex", flexDirection: "column" };
const iconBtn = { background: "transparent", border: "none", color: C.text, width: 36, height: 36, borderRadius: 18, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" };
const rowStyle = { display: "flex", alignItems: "center", gap: SP.md, background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.sm, padding: `${SP.sm}px ${SP.md}px` };
const inputStyle = { flex: 1, width: "100%", height: 44, background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.md, color: C.text, padding: `0 ${SP.md}px`, fontSize: 14, outline: "none", fontFamily: "inherit", boxSizing: "border-box" };
const selectStyle = { flex: 1, width: "100%", height: 44, background: C.surface, border: `1px solid ${C.border}`, borderRadius: R.md, color: C.text, padding: `0 ${SP.md}px`, fontSize: 13, fontFamily: "inherit", cursor: "pointer" };
const btnLime = { height: 40, borderRadius: R.md, background: C.lime, color: C.onLime, border: "none", fontWeight: 800, fontSize: 12, letterSpacing: 1, textTransform: "uppercase", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, cursor: "pointer", padding: `0 ${SP.md}px` };
const btnLimeSm = { ...btnLime, height: 30, fontSize: 11 };
const btnGhost = { height: 40, borderRadius: R.md, background: "transparent", color: C.textMuted, border: `1px solid ${C.borderStrong}`, fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: "uppercase", cursor: "pointer", padding: `0 ${SP.md}px` };
const btnDangerSm = { width: 32, height: 32, borderRadius: R.sm, background: "transparent", color: C.error, border: `1px solid ${C.border}`, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" };
const btnIconSm = { width: 32, height: 32, borderRadius: R.sm, background: "transparent", color: C.text, border: `1px solid ${C.border}`, display: "inline-flex", alignItems: "center", justifyContent: "center", cursor: "pointer" };
const guestPill = { background: C.surface3, color: C.textMuted, fontSize: 9, fontWeight: 700, letterSpacing: 1, padding: "2px 6px", borderRadius: R.pill, textTransform: "uppercase", marginLeft: 6 };
